import { CloseCircleFilled, CloseOutlined } from '@ant-design/icons';
import { Alert, type AlertProps, Space } from 'antd';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ErrorsList } from './ErrorsList.tsx';

const StyledAlert = styled(Alert)`
  margin-bottom: 24px;

  .ant-alert-close-icon {
    &:focus-visible {
      outline: 2px solid ${props => props.theme.focusOutlineColor};
      outline-offset: 5px;
      border-radius: ${props => props.theme.borderRadius}px;
    }
  }
`;

const AlertIconSpan = styled.div`
  color: ${props => props.theme.colorError};
  font-size: 24px;
  height: 24px;
  line-height: 24px;
`;
const DescriptionContainer = styled.div`
  margin-left: 36px;
`;

export interface AlertMessageProps
  extends Omit<AlertProps, 'description' | 'message'> {
  errors?: ReactNode[];
  /** Testo personalizzato per l'header dell'alert. Se omesso, usa il messaggio predefinito con il conteggio errori. */
  messageText?: ReactNode;
}

/**
 * Messaggio di errore
 */
export function AlertMessage(props: AlertMessageProps) {
  const { errors, messageText, ...otherProps } = props;
  const { t } = useTranslation();

  if (!errors || errors.length === 0) return null;

  return (
    <StyledAlert
      message={
        <Space size={12}>
          <AlertIconSpan>
            <CloseCircleFilled aria-label="Errore" aria-hidden />
          </AlertIconSpan>
          <span>
            {messageText ??
              t(
                'form.alert-message.content',
                'Sono presenti errori di compilazione ({{errors_count}})',
                { errors_count: errors.length },
              )}
          </span>
        </Space>
      }
      closable
      closeIcon={
        <CloseOutlined
          aria-label={t('alert-message.error.close-icon-label', 'Chiudi')}
        />
      }
      description={
        <DescriptionContainer>
          <ErrorsList errors={errors} />
        </DescriptionContainer>
      }
      type="error"
      {...otherProps}
    />
  );
}
